import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { InterfaceIndicator } from 'app/models/indicator.model';
import { InterfaceMesurement } from 'app/models/mesurement.model';
import { IndicatorService } from 'app/services/indicator.service';
import { timezone } from 'assets/constants/timezone';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import * as dayjs from 'dayjs';
import * as utc from 'dayjs/plugin/utc';
import * as timezoneDayJs from 'dayjs/plugin/timezone';
dayjs.extend(utc);
dayjs.extend(timezoneDayJs);

@Component({
  selector: 'app-indicator-detail',
  templateUrl: './indicator-detail.component.html',
  styleUrls: ['./indicator-detail.component.scss']
})
export class IndicatorDetailComponent implements OnInit, OnDestroy {

  chartOptions!: any;
  currentWeek: number;
  currentYear: number;
  mesurements: InterfaceMesurement[] = [];
  subscriptions: Subscription[] = [];
  @Input() data: any;

  constructor(
    protected indicatorService: IndicatorService,
    public ngbActiveModal: NgbActiveModal
  ) {
    this.currentWeek = dayjs().tz(timezone).isoWeek();
    this.currentYear = dayjs().tz(timezone).year();
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.indicatorService.getAllMesurementOfIndicator(this.data.indicator.id).pipe(take(1)).subscribe((result: InterfaceMesurement[]) => {
        this.mesurements = result;
        this.getPreviousValues();
      }, (error: any) => {
        console.error(error);
      })
    );
  }

  async getMesurement(weekNumber: number, yearNumber: number): Promise<any> {
    return new Promise((resolve) => {
      this.subscriptions.push(
        this.indicatorService.getMesurement(this.data.indicator.id, yearNumber, weekNumber).pipe(take(1)).subscribe((result: InterfaceMesurement[]) => {
          resolve(result[0]);
        }, (error: any) => {
          console.error(error);
        })
      );
    });
  }

  async getPreviousValues(): Promise<void> {
    let doFourTimes = 0;
    let previousValues: any = [];
    do {
      await this.getMesurement(this.currentWeek - doFourTimes, this.currentYear).then((result) => {
        previousValues.push(result);
      });
      doFourTimes++;
    } while (doFourTimes < 4);
    this.chartOptions = {
      xAxis: {
        type: 'category',
        data: [this.currentWeek - 3, this.currentWeek - 2, this.currentWeek - 1, this.currentWeek]
      },
      yAxis: {
        type: 'value'
      },
      series: [
        {
          data: [
            previousValues[3] && previousValues[3].result ? previousValues[3].result : 0,
            previousValues[2] && previousValues[2].result ? previousValues[2].result : 0,
            previousValues[1] && previousValues[1].result ? previousValues[1].result : 0,
            previousValues[0] && previousValues[0].result ? previousValues[0].result : 0
          ],
          type: 'line',
          color: this.getColorLine(this.data.indicator, previousValues[0] ? previousValues[0] : { result: 0 })
        }
      ],
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'line',
          label: {
            backgroundColor: '#303f9f'
          }
        }
      }
    };
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub: Subscription) => {
      sub.unsubscribe();
    });
  }

  returnDateFormat(unix: number): string {
    return dayjs.unix(unix).tz(timezone).format("HH:mm DD/MM/YYYY");
  }

  downloadFile(url: string): void {
    window.open(url);
  }

  returnChangedFormat(amount: number): string {
    let formatter: any;
    switch (this.data.indicator.type) {
      case 1:
        formatter = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 2,
          minimumFractionDigits: 2
        });
        if (amount) {
          return formatter.format(amount);
        } else {
          return formatter.format(0);
        }
      case 2:
        formatter = new Intl.NumberFormat("en-US", {
          style: 'percent'
        });
        if (amount) {
          return formatter.format(amount / 100);
        } else {
          return formatter.format(0 / 100);
        }
      case 3:
        return amount.toString();
      default:
        return "";
    }
  }

  getColorLine(indicator: InterfaceIndicator, mesurement: InterfaceMesurement): string {
    // 1 = increase
    // 2 = decrease
    if (indicator.checkType === 1) {
      if (mesurement.result > indicator.goal!) {
        return "#00c853";
      } else if (mesurement.result < indicator.goal! && mesurement.result > indicator.minValue!) {
        return "#ffff00";
      } else {
        return "#d50000";
      }
    } else {
      if (mesurement.result < indicator.goal!) {
        return "#00c853";
      } else if (mesurement.result > indicator.goal! && mesurement.result < indicator.minValue!) {
        return "#ffff00";
      } else {
        return "#d50000";
      }
    }
  }

  // return month name
  returnMonthFormat(monthNumber: number): string {
    switch (monthNumber) {
      case 1:
        return "Enero";
      case 2:
        return "Febrero";
      case 3:
        return "Marzo";
      case 4:
        return "Abril";
      case 5:
        return "Mayo";
      case 6:
        return "Junio";
      case 7:
        return "Julio";
      case 8:
        return "Agosto";
      case 9:
        return "Septiembre";
      case 10:
        return "Octubre";
      case 11:
        return "Noviembre";
      case 12:
        return "Diciembre";
      default:
        return "Error";
    }
  }

}
